import React, { useState } from 'react';

function UpgradeModal({
  selectedNFTs,
  onClose,
  onConfirm,
  onDone,
  confirmTitle = 'Confirm Upgrade',
  confirmMessage = `Are you sure you want to upgrade ${selectedNFTs.length} NFT${selectedNFTs.length > 1 ? 's' : ''}?`,
  confirmButtonText = `UPGRADE NFT${selectedNFTs.length > 1 ? 's' : ''}`,
  cancelButtonText = 'Cancel',
  statusUpgradingText = '⏳ Upgrading your NFT(s)...',
  statusCompleteText = '✅ Upgrade Complete!',
  completeButtonText = 'Okay',
  danger = false // make cancel button red if danger mode
}) {
  const [status, setStatus] = useState('confirm');
  const [result, setResult] = useState(null);

  const handleConfirm = async () => {
    setStatus('upgrading');
    try {
      const upgradeResult = await onConfirm();
      setResult(upgradeResult);
      setStatus('complete');
    } catch (err) {
      onClose();
    }
  };

  const handleCancel = async () => {
    // Log user cancellation
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/nft?` +
        new URLSearchParams({ action: 'log-upgrade-failure' }),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            wallet: window.ethereum?.selectedAddress,
            upgrade_type: selectedNFTs.length > 1 ? 'recipe_upgrade' : 'single_upgrade',
            original_nfts: selectedNFTs.map(nft => ({
              collection: nft.collection,
              tokenId: nft.tokenId.toString()
            })),
            error_message: 'User cancelled upgrade',
            error_stage: 'user_cancellation',
            user_agent: navigator.userAgent
          })
        }
      );
    } catch (logError) {
      console.error('Failed to log cancellation:', logError);
    }
    
    onClose();
  };

  const handleOkay = () => {
    if (onDone) {
      onDone();
    } else {
      onClose();
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        {status === 'confirm' && (
          <>
            <h2>{confirmTitle}</h2>
            
            {/* Show NFTs being upgraded */}
            {selectedNFTs && selectedNFTs.length > 0 && (
              <>
                <p style={{ marginBottom: '1rem' }}>
                  You're about to burn the following {selectedNFTs.length} NFT{selectedNFTs.length > 1 ? 's' : ''}:
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: '1rem 0' }}>
                  {selectedNFTs.map(nft => (
                    <li
                      key={`${nft.collection}-${nft.tokenId}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        margin: '0.5rem 0',
                        padding: '0.5rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '4px'
                      }}
                    >
                      {nft.image && (
                        <img
                          src={nft.image}
                          alt={nft.title || nft.collection}
                          style={{
                            width: '32px',
                            height: '32px',
                            objectFit: 'contain',
                            marginRight: '8px',
                            borderRadius: '4px'
                          }}
                        />
                      )}
                      <span>
                        {nft.title || nft.collection} — Token <strong>{nft.tokenId}</strong>
                        {nft.rarity && (
                          <span style={{ 
                            marginLeft: '0.5rem',
                            color: '#8a2be2',
                            fontSize: '0.8rem',
                            fontWeight: 'bold'
                          }}>
                            ({nft.rarity})
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            
            <p>{confirmMessage}</p>
            <div className="modal-buttons" style={{ marginTop: '1rem' }}>
              <button
                className="no-btn"
                style={{ backgroundColor: danger ? '#e74c3c' : '#e74c3c', color: '#fff' }}
                onClick={handleCancel}
              >
                {cancelButtonText}
              </button>
              <button
                className="rainbowBtn"
                style={{ backgroundColor: danger ? '#2ecc71' : '#007bff', color: '#fff' }}
                onClick={handleConfirm}
              >
                {confirmButtonText}
              </button>
            </div>
          </>
        )}

        {status === 'upgrading' && (
          <>
            <h2>{statusUpgradingText}</h2>
            <div className="loading-spinner">Processing...</div>
          </>
        )}

        {status === 'complete' && (
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>
              {statusCompleteText}
            </h2>
            
            {result && result.minted_item && (
              <div style={{
                marginBottom: '2rem',
                padding: '1.5rem',
                background: 'rgba(138, 43, 226, 0.1)',
                border: '1px solid rgba(138, 43, 226, 0.3)',
                borderRadius: '1rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                {/* Show all minted items if multiple */}
                {result.minted_item.all_minted_items && result.minted_item.all_minted_items.length > 1 ? (
                  <>
                    <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '1rem', color: '#8a2be2' }}>
                      Minted Items:
                    </h3>
                    {result.minted_item.all_minted_items.map((item, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        padding: '0.75rem',
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '0.5rem',
                        width: '100%'
                      }}>
                        {item.image && (
                          <img 
                            src={item.image} 
                            alt={item.name}
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'contain',
                              borderRadius: '0.25rem'
                            }}
                          />
                        )}
                        <div style={{ textAlign: 'left', flex: 1 }}>
                          <div style={{ fontWeight: 'bold', color: '#8a2be2' }}>{item.name}</div>
                          <div style={{ fontSize: '0.9rem', color: '#ccc' }}>Token ID: {item.token_id}</div>
                          {item.rarity && (
                            <div style={{ fontSize: '0.8rem', color: '#8a2be2', fontWeight: 'bold' }}>
                              {item.rarity}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    {/* Single item display */}
                    {result.minted_item.image && (
                      <img 
                        src={result.minted_item.image} 
                        alt={result.minted_item.name}
                        style={{
                          width: '150px',
                          height: '150px',
                          objectFit: 'contain',
                          borderRadius: '0.5rem'
                        }}
                      />
                    )}
                    <div style={{ textAlign: 'center' }}>
                      <h3 style={{ 
                        fontSize: '1.2rem', 
                        fontWeight: 'bold',
                        marginBottom: '0.5rem',
                        color: '#8a2be2'
                      }}>
                        {result.minted_item.name}
                      </h3>
                      <p style={{ marginBottom: '0.25rem' }}>
                        Collection: {result.minted_item.collection}
                      </p>
                      <p style={{ marginBottom: '0.25rem' }}>
                        Token ID: {result.minted_item.token_id}
                      </p>
                      {result.minted_item.rarity && (
                        <p style={{ 
                          marginBottom: '0.25rem',
                          color: '#8a2be2',
                          fontWeight: 'bold'
                        }}>
                          Rarity: {result.minted_item.rarity}
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* Transaction Links Section */}
                <div style={{ 
                  width: '100%', 
                  borderTop: '1px solid rgba(138, 43, 226, 0.3)', 
                  paddingTop: '1rem',
                  marginTop: '1rem'
                }}>
                  {/* Burn Transaction Links */}
                  {result.minted_item.burn_tx_hashes && result.minted_item.burn_tx_hashes.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <strong style={{ color: '#ff6b6b' }}>Burn Transaction{result.minted_item.burn_tx_hashes.length > 1 ? 's' : ''}:</strong>
                      <div style={{ marginTop: '0.5rem' }}>
                        {result.minted_item.burn_tx_hashes.map((txHash, index) => (
                          <div key={txHash} style={{ marginBottom: '0.25rem' }}>
                            {result.minted_item.burn_tx_hashes.length > 1 && (
                              <span style={{ fontSize: '0.9rem', color: '#ccc' }}>
                                Token {result.minted_item.burn_token_ids?.[index] || index + 1}:{' '}
                              </span>
                            )}
                            <a
                              href={`https://polygonscan.com/tx/${txHash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#ff6b6b',
                                textDecoration: 'underline',
                                fontSize: '0.9rem'
                              }}
                            >
                              View Burn on PolygonScan
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Mint Transaction Link */}
                  {result.minted_item.tx_hash && (
                    <div>
                      <strong style={{ color: '#22c55e' }}>Mint Transaction:</strong>
                      <div style={{ marginTop: '0.5rem' }}>
                        <a
                          href={`https://polygonscan.com/tx/${result.minted_item.tx_hash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#22c55e',
                            textDecoration: 'none',
                            display: 'inline-block',
                            padding: '0.5rem 1rem',
                            border: '1px solid rgba(34, 197, 94, 0.3)',
                            borderRadius: '0.5rem',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(34, 197, 94, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'transparent';
                          }}
                        >
                          View Mint on PolygonScan
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <button 
              className="rainbowBtn" 
              onClick={handleOkay}
              style={{
                fontSize: '1.1rem',
                padding: '0.75rem 2rem'
              }}
            >
              {completeButtonText}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default UpgradeModal;
